<script lang="ts">
  /**
   * DateRangeNav - ‹ label › + Today, with an optional trailing slot (e.g.
   * "Show archived"). Shared by Habits and the Cycles History tab.
   *
   * On phones (<768px) the label is the short form from lib/dateRangeLabel
   * ("Sep 24–28"; a single day, `start === end`, reads "Mon, Sep 28") and never
   * wraps: a year other than this one goes on the second line, next to the
   * context ("2025 · 40 weeks prior"). Arrows are 44px chevrons and Today is a
   * pill. Above that it keeps the desktop look: ← label → Today, with the
   * page's own `label` if it passes one.
   *
   * Spacing hooks for the desktop row: --drn-gap, --drn-label-min-width and
   * --drn-today-gap (extra space before Today).
   */
  import type { Snippet } from 'svelte';
  import { viewport } from '../lib/stores/viewport';
  import { formatShortDateRange, formatShortDay, shortDateRangeParts } from '../lib/dateRangeLabel';

  interface Props {
    /** First visible date, `YYYY-MM-DD`. */
    start: string;
    /** Last visible date, `YYYY-MM-DD` (inclusive). */
    end: string;
    /** Desktop label. Defaults to the short label; phones always use the short one. */
    label?: string;
    /** Today's date (`YYYY-MM-DD`): the short label shows the year only when it differs. */
    today?: string;
    /** Grey line under the label, e.g. "3 weeks prior". */
    context?: { text: string; tone: 'prior' | 'future' };
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    /** Accessible names of the arrows. */
    previousLabel?: string;
    nextLabel?: string;
    nextDisabled?: boolean;
    /** Rendered after Today (e.g. a "Show archived" toggle). */
    trailing?: Snippet;
  }

  let {
    start,
    end,
    label,
    today,
    context,
    onPrevious,
    onNext,
    onToday,
    previousLabel = 'Previous',
    nextLabel = 'Next',
    nextDisabled = false,
    trailing,
  }: Props = $props();

  let short = $derived(shortDateRangeParts(start, end, today));
  let shownLabel = $derived(
    $viewport.isMobile
      ? start === end
        ? formatShortDay(start)
        : short.range
      : (label ?? formatShortDateRange(start, end, today)),
  );
</script>

<div class="drn" class:mobile={$viewport.isMobile} role="group" aria-label="Date range">
  <button type="button" class="drn-btn drn-arrow" aria-label={previousLabel} onclick={onPrevious}>
    {#if $viewport.isMobile}
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 5-7 7 7 7" /></svg>
    {:else}←{/if}
  </button>
  <strong class="drn-label">
    <span class="drn-text">{shownLabel}</span>
    {#if $viewport.isMobile && short.year !== null}
      <small class="drn-sub">
        <span class="drn-year">{short.year}</span>
        {#if context}
          <span class="drn-sep" aria-hidden="true">·</span>
          <span class="drn-context {context.tone}">{context.text}</span>
        {/if}
      </small>
    {:else if context}
      <small class="drn-context {context.tone}">{context.text}</small>
    {/if}
  </strong>
  <button
    type="button"
    class="drn-btn drn-arrow"
    aria-label={nextLabel}
    disabled={nextDisabled}
    onclick={onNext}
  >
    {#if $viewport.isMobile}
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7" /></svg>
    {:else}→{/if}
  </button>
  <button type="button" class="drn-btn drn-today" onclick={onToday}>Today</button>
  {@render trailing?.()}
</div>

<style>
  /* Desktop: the look Habits and Cycles › History had before this component. */
  .drn {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: var(--drn-gap, 0.5rem);
    min-width: 0;
  }

  .drn-btn {
    padding: 0.375rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: white;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.15s;
  }

  .drn-btn:hover {
    background: #f3f4f6;
  }

  .drn-today {
    margin-left: var(--drn-today-gap, 0);
  }

  .drn-label {
    position: relative;
    min-width: var(--drn-label-min-width, 210px);
    text-align: center;
  }

  .drn-context {
    position: absolute;
    top: calc(100% + 1px);
    left: 0;
    right: 0;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }

  .drn-context.prior {
    color: #4f6fd8;
  }

  .drn-context.future {
    color: #b45309;
  }

  /* Phone: ‹ label › Today on one 44px row. The label never wraps. */
  .drn.mobile {
    gap: 2px;
  }

  .drn.mobile .drn-arrow {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: none;
    color: #4b5563;
    -webkit-tap-highlight-color: transparent;
  }

  .drn.mobile .drn-arrow:active {
    background: #f3f4f6;
  }

  .drn.mobile .drn-arrow:disabled {
    color: #d1d5db;
    cursor: default;
  }

  .drn.mobile .drn-arrow svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .drn.mobile .drn-label {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    color: #111827;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
  }

  .drn.mobile .drn-text {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .drn.mobile .drn-context,
  .drn.mobile .drn-sub {
    position: static;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
  }

  .drn-sub {
    display: flex;
    gap: 4px;
    color: #6b7280;
  }

  /* Same pill as the Timeline top bar's Today, with a 44px hit area. */
  .drn.mobile .drn-today {
    position: relative;
    flex-shrink: 0;
    height: 32px;
    margin: 0 4px 0 2px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    color: #2563eb;
    font-size: 14px;
    -webkit-tap-highlight-color: transparent;
  }

  .drn.mobile .drn-today::after {
    content: '';
    position: absolute;
    inset: -6px -2px;
  }

  .drn.mobile .drn-btn:hover {
    background: none;
  }

  .drn.mobile .drn-today:active {
    background: #f3f4f6;
  }
</style>
